/**
 * Frontend-specific stepped HP utilities.
 *
 * Shared types and Array↔Record conversions live in ./stepConversions.ts
 * (mirrored in graphql/src/util/viewer/hangingProtocol/stepConversions.ts).
 * This file adds frontend-only concerns:
 *   - Mutation helpers (stepToGqlInput, applyMirrorGroupsToStepViewports)
 *   - Viewport filtering (isViewportInLayoutBounds, isEmptyViewport)
 *   - Helper functions (hasSubSteps)
 */

import type {
  StepViewportEntryInput,
  StepLayoutEntryInput,
  StepSubStepInput,
  StepConfigurationInput,
} from 'generated/graphql';
import { ViewType, AllowedViewportType } from 'generated/graphql';
import type { ViewportTypeKeys } from 'config/constants';
import {
  assignMirrorGroupIds,
  hasNoResolvedSeries,
  type ViewportSeriesIds,
} from 'domains/viewer/Viewer/StudiesBar/HangingProtocols/utils/viewportsConfigToHangingProtocol';
import { stripTypename } from 'utils/stripTypename';
import type {
  StepConfiguration,
  ViewportConfigurationEntry,
  GqlStepConfiguration,
  StepViewportEntry,
} from './stepConversions';
import { parseLayoutString, formatLayoutString } from './stepConversions';
import { calculateSubStepTotal } from './hpStepController';
import { viewportIdToConfig } from 'domains/viewer/Viewer/viewerUtils';

// Re-export shared types and conversions so existing imports don't break
export { viewportsArrayToRecord, layoutsArrayToRecord, stepFromGqlFormat } from './stepConversions';
export { parseLayoutString, formatLayoutString };
export type {
  StepConfigDetails,
  ViewportConfigurationEntry,
  SubStep,
  StepConfiguration,
  GqlStepConfiguration,
  StepViewportEntry,
  StepLayoutEntry,
  StepSubStep,
} from './stepConversions';

// ── view_type bridges ────────────────────────────────────────────────────
// A step viewport's persisted `view_type` is `AllowedViewportType`. It and the
// frontend `ViewType`/`ViewportTypeKeys` share identical string values, so
// conversion is identity.

const ALLOWED_VIEWPORT_TYPE_VALUES = new Set<string>(Object.values(AllowedViewportType));

/** True when `value` is a valid `AllowedViewportType` enum value. */
export function isAllowedViewportType(value: unknown): value is AllowedViewportType {
  return typeof value === 'string' && ALLOWED_VIEWPORT_TYPE_VALUES.has(value);
}

/**
 * Converts a frontend view type into the persistable `AllowedViewportType`.
 * Returns null for unknown/unsupported values so we never persist a string that
 * isn't a valid GraphQL enum member.
 */
export function toAllowedViewportType(
  viewType: Nullable<ViewType | ViewportTypeKeys | string>
): AllowedViewportType | null {
  if (viewType == null) return null;
  return isAllowedViewportType(viewType) ? viewType : null;
}

/** Reads a persisted `view_type` back into a frontend `ViewType` (default 2D). */
export function fromAllowedViewportType(viewType: Nullable<AllowedViewportType>): ViewType {
  return (viewType as unknown as ViewType) ?? ViewType.TwoDDre;
}

/**
 * Returns true if the given viewport ID falls within the bounds of the provided layouts.
 * Viewport IDs follow the format "Viewport-{windowId}-{row}-{col}".
 */
export function isViewportInLayoutBounds(
  viewportId: string,
  layouts: Record<string, string>
): boolean {
  const { windowId, rowIndex, colIndex } = viewportIdToConfig(viewportId);

  const layoutStr = layouts[windowId];
  if (layoutStr == null) return false;

  const [rows, cols] = parseLayoutString(layoutStr);
  return rowIndex < rows && colIndex < cols;
}

/**
 * Returns true if the viewport entry is effectively empty (unassigned slot).
 * A viewport is empty when it has no display name AND no series criteria.
 */
export function isEmptyViewport(entry: ViewportConfigurationEntry): boolean {
  const hasDisplayName = entry.display_name != null && entry.display_name !== '';
  const hasCriteria = Boolean(entry.criteria?.length);
  const hasSeriesCriteriaText =
    entry.series_criteria_source_text != null && entry.series_criteria_source_text !== '';
  return !hasDisplayName && !hasCriteria && !hasSeriesCriteriaText;
}

/**
 * Drops mirror group ids that no longer name a group, i.e. that fewer than two of these viewports
 * carry.
 *
 * A mirror group means "these viewports hang the same series", so one member is not a group -- and
 * the backend enforces exactly that, rejecting the whole step write with
 * `400 Mirror group N contains fewer than two viewports`. Nothing recomputes the ids on the way out:
 * they are carried through from the stored step, so any filtering above this can strip a group down
 * below two members without the survivors noticing.
 *
 * Shrinking the grid is the way a reader reaches it. On a 2x2 step whose group 1 is
 * {0-0-1, 0-1-0, 0-1-1}, switching to 2x1 leaves only 0-0-0 and 0-1-0 in bounds, so group 1 goes out
 * holding one viewport and the save is rejected. `executeSaveOnBackend` does roll the dirty dot back
 * and toast the failure -- this isn't a silent loss -- but it is a confusing round trip: the "Step
 * updated with your changes" toast fires optimistically the moment the save is queued, and the
 * rejection and its own toast land ~2s later, after the reader has moved on believing the save
 * worked. Dropping the group before it ever reaches the network avoids that entirely.
 */
function dropUndersizedMirrorGroups(viewports: StepViewportEntryInput[]): StepViewportEntryInput[] {
  const membersPerGroup = new Map<number, number>();
  for (const viewport of viewports) {
    const groupId = viewport.viewport_mirror_group_id;
    if (groupId == null) continue;
    membersPerGroup.set(groupId, (membersPerGroup.get(groupId) ?? 0) + 1);
  }
  if ([...membersPerGroup.values()].every((count) => count >= 2)) return viewports;

  return viewports.map((viewport) => {
    const groupId = viewport.viewport_mirror_group_id;
    if (groupId == null || (membersPerGroup.get(groupId) ?? 0) >= 2) return viewport;
    return { ...viewport, viewport_mirror_group_id: null };
  });
}

/**
 * Convert internal Record<viewportId, VCE> to GQL StepViewportEntryInput[].
 * Strips is_clear entries, effectively-empty viewports, and viewports whose
 * coordinates fall outside the layout bounds, then drops any mirror group the
 * filtering left with a single member (see {@link dropUndersizedMirrorGroups}).
 */
export function viewportsRecordToInputArray(
  viewports: Record<string, ViewportConfigurationEntry>,
  layouts: Record<string, string>
): StepViewportEntryInput[] {
  const inBounds = Object.entries(viewports)
    .filter(([viewportId, entry]) => {
      if (entry.is_clear) return false;
      if (isEmptyViewport(entry)) return false;
      if (!isViewportInLayoutBounds(viewportId, layouts)) return false;
      return true;
    })
    .map(([viewportId, entry]) => ({
      viewport_id: viewportId,
      display_name: entry.display_name ?? viewportId,
      study_smid: entry.study_smid,
      series_smid: entry.series_smid,
      view_type: entry.view_type ?? null,
      series_criteria_source_text: entry.series_criteria_source_text ?? null,
      comparison_index: entry.comparison_index ?? null,
      criteria: entry.criteria ?? null,
      stack_smid: entry.stack_smid ?? null,
      viewport_mirror_group_id: entry.viewport_mirror_group_id ?? null,
    }));

  return dropUndersizedMirrorGroups(inBounds);
}

/** Convert internal Record<windowId, layoutString> to GQL StepLayoutEntryInput[]. */
export function layoutsRecordToInputArray(layouts: Record<string, string>): StepLayoutEntryInput[] {
  return Object.entries(layouts).map(([windowId, layout]) => ({
    window_id: windowId,
    layout,
  }));
}

/**
 * Recomputes a step's mirror group ids from the series each viewport actually holds, falling back to a
 * viewport's criteria where no series resolved.
 *
 * This used to group by the viewport's *criteria* alone, on the reasoning that two viewports matching
 * the same rule must resolve to the same series. They need not, and the difference is user-visible in
 * both directions:
 *
 * - Two genuinely different series routinely classify identically, because the form classifies by
 *   series description and a study can carry two series with one description. Grouping them told the
 *   backend to hang one of them twice (VX-5005).
 * - Conversely the flat path emitted no group at all, so one series hung deliberately in two viewports
 *   came back as two different series (VX-5006).
 *
 * A resolved series is the only thing that separates those two cases, so it decides every viewport that
 * has one. Criteria decide the rest: a viewport that resolved to nothing carries no other statement of
 * what it was meant to hold. The two tiers are keyed separately, so the fallback can never pull a
 * resolved viewport into a group its own series rules out. See {@link assignMirrorGroupIds}.
 *
 * `seriesIds` is optional and its absence means "no information", not "nothing resolved": the existing
 * `viewport_mirror_group_id` values are then left exactly as they are. That is what keeps a save from
 * stripping the groups off steps the user never opened, whose viewports this window has never
 * resolved. `hasNoResolvedSeries` folds the "map supplied but empty" case into the same branch, so a
 * step that resolves nothing keeps the groups it has rather than having criteria-derived ones written
 * over them -- the criteria fallback informs a group, it never overwrites one from absent evidence.
 */
export function applyMirrorGroupsToStepViewports(
  viewports: StepViewportEntryInput[],
  seriesIds?: Nullable<ViewportSeriesIds>
): StepViewportEntryInput[] {
  if (hasNoResolvedSeries(seriesIds)) return viewports;

  const mirrorGroupIds = assignMirrorGroupIds(
    viewports.map((vp) => ({
      id: vp.viewport_id,
      comparisonIndex: vp.comparison_index ?? null,
      criteria: vp.criteria ?? undefined,
    })),
    seriesIds as ViewportSeriesIds
  );

  return viewports.map((vp, i) => ({
    ...vp,
    viewport_mirror_group_id: mirrorGroupIds[i],
  }));
}

/**
 * Convert an internal StepConfiguration to GQL StepConfigurationInput for mutations.
 *
 * `seriesIds` describes what *this step's* viewports are showing; omit it for a step whose viewports
 * this window cannot resolve, and the step's stored mirror groups are carried through untouched.
 * Sub-steps share the parent step's map: a sub-step re-targets which prior a viewport reads from, not
 * which grid slot holds what, so slot identity is the same question in both.
 */
export function stepToGqlInput(
  step: StepConfiguration,
  seriesIds?: Nullable<ViewportSeriesIds>
): StepConfigurationInput {
  return {
    smid: step.smid,
    display_name: step.display_name,
    layouts: layoutsRecordToInputArray(step.layouts),
    config_details: step.config_details,
    viewports: applyMirrorGroupsToStepViewports(
      viewportsRecordToInputArray(step.viewports, step.layouts),
      seriesIds
    ),
    sub_steps: step.sub_steps
      ? step.sub_steps.map((ss) => ({
          viewports: applyMirrorGroupsToStepViewports(
            viewportsRecordToInputArray(ss, step.layouts),
            seriesIds
          ),
        }))
      : null,
  };
}

/**
 * Converts a `StepConfigurationInput` (mutation-input shape, `view_type` a raw string) into the
 * shape the `HangingProtocol` cache fragment expects (query-output shape, `view_type` the
 * `AllowedViewportType` enum), so a save's payload can be merged directly into a cached step
 * without a type mismatch.
 *
 * `sub_steps` is preserved as `null` when the input has none, rather than mapped to `undefined` --
 * the two write differently under Apollo's cache merge semantics, and only `null` reliably clears
 * the cached field.
 */
export function toCachedStepPayload(gqlPayload: StepConfigurationInput) {
  // Deliberately not typed as `StepViewportEntryInput` -- that type's `view_type` is the raw
  // input string, and re-declaring it here would widen `toAllowedViewportType`'s enum return
  // back to `string`, reintroducing the mismatch this function exists to fix.
  const withCachedViewType = (viewport: StepViewportEntryInput) => ({
    ...viewport,
    view_type: toAllowedViewportType(viewport.view_type),
  });

  return {
    ...gqlPayload,
    viewports: gqlPayload.viewports.map(withCachedViewType),
    sub_steps: gqlPayload.sub_steps
      ? gqlPayload.sub_steps.map((subStep) => ({
          ...subStep,
          viewports: subStep.viewports.map(withCachedViewType),
        }))
      : gqlPayload.sub_steps,
  };
}

/**
 * Projects a step in GQL *output* shape (`StepConfiguration`, as returned by
 * matchHangingProtocolByWorklistsAndStudies) onto `StepConfigurationInput` for use in a mutation.
 *
 * Needed because the two types are not interchangeable: `StepConfiguration` also carries `order`,
 * which `StepConfigurationInput` does not define, so passing a fetched step straight back into a
 * mutation makes GraphQL reject the entire variable with BAD_USER_INPUT (VX-4713). This is an
 * allowlist of the input type's own fields rather than a "delete the fields we know are bad" strip,
 * so a future output-only field can't reintroduce the same failure. `layouts` and `config_details`
 * have identical field names on the input side and so only need `__typename` removed; viewports go
 * through {@link gqlViewportToInput} because `StepViewportEntry` carries the output-only
 * `match_justification` (VX-4865).
 *
 * Distinct from {@link stepToGqlInput}, which converts the internal Record-based
 * `StepConfiguration` used for frontend step state.
 */
export function gqlStepToInput(step: GqlStepConfiguration): StepConfigurationInput {
  return {
    smid: step.smid,
    display_name: step.display_name,
    layouts: stripTypename(step.layouts) as StepLayoutEntryInput[],
    config_details: stripTypename(step.config_details),
    viewports: step.viewports.map(gqlViewportToInput),
    // Pass a nullish sub_steps straight through rather than normalizing it: `null` and `undefined` mean different
    // things to GraphQL (explicitly clear vs. leave alone), and the previous stripTypename call preserved both.
    sub_steps:
      step.sub_steps == null
        ? step.sub_steps
        : step.sub_steps.map((ss) => ({ viewports: ss.viewports.map(gqlViewportToInput) })),
  };
}

/**
 * Projects one output-shape step viewport onto `StepViewportEntryInput`. An allowlist of the input
 * type's own fields, so output-only members — `match_justification`, `__typename` — are dropped
 * rather than forwarded into a mutation variable.
 */
function gqlViewportToInput(viewport: StepViewportEntry): StepViewportEntryInput {
  return {
    viewport_id: viewport.viewport_id,
    display_name: viewport.display_name,
    study_smid: viewport.study_smid,
    series_smid: viewport.series_smid,
    view_type: viewport.view_type,
    series_criteria_source_text: viewport.series_criteria_source_text,
    criteria: stripTypename(viewport.criteria),
    stack_smid: viewport.stack_smid,
    viewport_mirror_group_id: viewport.viewport_mirror_group_id,
    comparison_index: viewport.comparison_index,
  };
}

/**
 * Whether a server-provided HP's steps name real backend step rows.
 *
 * The backend's /hang-cases endpoint normalizes a NON-stepped HP into a single synthetic step
 * (`smid: null`, `display_name: null`) built from the HP's flat layouts and viewports, so a flat HP
 * also arrives with a non-empty `steps` array and cannot be told apart by length alone. Only a
 * persisted step row has an smid.
 *
 * Mutation payloads have to make that distinction: sending `steps` -- or `preserveExistingSteps` --
 * makes the GraphQL layer null the HP's `layouts` and omit its `viewports`, and the backend saves
 * the HP before it creates any step rows, so an HP that had none is left with neither and
 * `HangingProtocolV2.clean()` rejects the update ("HP must have either layouts, or steps"). That is
 * every edit of a flat HP failing, phase-2 renames included (VX-4889).
 *
 * Only meaningful for steps as the server returned them -- both call sites read `hangingProtocol.steps`
 * off the GraphQL query result, never the form's client-side `hpFormStepConfigurations`. Steps the
 * user just created client-side carry a `step-…` placeholder smid (`CLIENT_STEP_SMID_PREFIX` in
 * `hpStepController.ts`) and name no row yet; a caller that ever mixes those into this function's
 * input would need to filter them out first, since this treats any non-null smid as persisted.
 */
export function hasPersistedSteps(
  steps: Nullable<ReadonlyArray<{ smid?: Nullable<string> }>>
): boolean {
  return steps?.some((step) => step.smid != null) ?? false;
}

// ============================================================================
// Helper functions
// ============================================================================

export type {
  StepViewportEntryInput,
  StepLayoutEntryInput,
  StepSubStepInput,
  StepConfigurationInput,
};

/**
 * Helper to check if a step has sub-steps for prior scrolling.
 */
export function hasSubSteps(step: Nullable<StepConfiguration>): boolean {
  return calculateSubStepTotal(step) > 0;
}
